import { action, setProperties } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { APIData, RecordAttributes, RecordData, Service } from 'webapp-types';
import { MetricType, MetricValueType, ModelName, TimeUnit } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import type { EnsureUniquenessSettings } from 'webapp/components/metric/edit/ensure-uniqueness';
import type { MetricTypePropertiesType } from 'webapp/components/metric/edit/metric-type-properties';

import type { Metric } from 'webapp/models-custom';
import type Source from 'webapp/models-custom/source';
import type MetricRecord from 'webapp/models/metric';

// supporting both QB metric definition component
// and google sheets wizard metric definition step
interface MetricProperties {
	format?: string;
	metricType?: string;
	granulationOrigin?: number | null;
	aggregationFunction?: string | null;
	changeInvert?: boolean;
	ensureUnique?: boolean;
	isAppend?: boolean;
	isHistoricalDataPersistenceEnabled?: boolean | null;
}

interface QueryBuilderFormMetricDefinitionBaseComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default abstract class QueryBuilderFormMetricDefinitionBaseComponent<T> extends Component<
	QueryBuilderFormMetricDefinitionBaseComponentInterface<T>
> {
	@service('store') declare storeService: Service.Store;
	@service declare popup: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;

	declare metric: Metric;

	abstract get model(): APIData.CustomQuery.MetricDefinitionValue | null;
	abstract get source(): Source;
	abstract get isDisabled(): boolean;
	abstract get isPeriodDataTypeHidden(): boolean;
	abstract get isAllValuesDataTypeHidden(): boolean;
	abstract save(data: APIData.CustomQuery.MetricDefinitionValue): void;

	get normalizedMetricSettingsData(): RecordAttributes<MetricRecord> {
		return this.getNormalizedMetricSettingsData();
	}

	get spaceAccessId(): number {
		return this.source.numericId!;
	}

	get showEnsureUnique(): boolean {
		return this.model?.settings.metricType === MetricValueType.EVENT;
	}

	get showHistoricalDataPersistence() {
		return this.source?.sourceType?.historicalDataPersistenceSupported;
	}

	setInitialData(): void {
		// set metric
		this.updateMetricFromModel();

		if (!this.model) {
			// fill defaults
			const defaults = this.metric.toSerializedJSON();
			next(this, () => {
				this.saveSettings(defaults);
			});
		}
	}

	// attributes the metric doesn't have yet stay undefined until the record created from them applies its defaults
	getNormalizedMetricSettingsData(): RecordAttributes<MetricRecord> {
		const sourceType = this.source.sourceType;

		// make a copy of settings, then noralize them for a Metric model
		let settings = copyObject(this.model || {}) as Record<string, unknown>;
		settings = Object.assign(settings, {
			objectType: MetricType.CUSTOM_QUERY,
			objectId: this.mmCustomQueriesService.generateTemporaryFakeId(),
			spaceAccessId: this.spaceAccessId,
			dataSourceTypeKey: sourceType.key,
		});

		if (!this.model) {
			if (this.isAllValuesDataTypeHidden) {
				// on new Salesforce qb in builder tab, set metrictype to general
				settings.metricType = MetricValueType.GENERAL;
				settings.granulationOrigin = TimeUnit.DAY;
			} else if (sourceType.metricBuilderIntegration) {
				// metricbuilder integrations - current by default (latest agg)
				settings.metricType = MetricValueType.CURRENT;
			}

			if (sourceType.isSheets || sourceType.isSqlishConnector) {
				settings.ensureUnique = true;
			}

			// Asana task ref: https://app.asana.com/0/1202359743778107/1207755749536066/f
			if (sourceType.historicalDataPersistenceSupported) {
				settings.isHistoricalDataPersistenceEnabled = false;
			} else {
				settings.isHistoricalDataPersistenceEnabled = null;
			}
		}

		const normalizedSettings = this.storeService.normalize(ModelName.METRIC, settings).data
			.attributes as RecordAttributes<MetricRecord>;
		return normalizedSettings;
	}

	parseSettingsToSave(
		metricSettings: RecordData.Metric.Serialized,
	): APIData.CustomQuery.MetricDefinitionValue {
		return {
			description: metricSettings.description || null,
			noReset: !!metricSettings.noReset,
			format: metricSettings.format || null,
			changeFormat: metricSettings.changeFormat || null,
			changeInvert: !!metricSettings.changeInvert,
			changeFunction: metricSettings.changeFunction,
			settings: metricSettings.settings,
		};
	}

	get infoTooltipText(): string {
		let text =
			'These selections determine how your data is handled. This especially impacts the way data is grouped and displayed for longer date ranges.';
		if (!this.accountService.hideHelpArticles) {
			text +=
				' <a href="https://developers.databox.com/metric-definition#aggregation-functions" target="_blank">Read more</a>';
		}
		return text;
	}

	updateMetricAndSave(properties: MetricProperties): void {
		const metric = this.metric;

		if (properties.metricType && properties.metricType !== MetricValueType.EVENT) {
			metric.ensureUnique = false; // reset to default FALSE value
			metric.isAppend = false; // reset to default FALSE value
		}

		setProperties(metric, properties as Pick<Metric, keyof MetricProperties>);
		this.saveSettings(metric.toSerializedJSON());
	}

	@action
	updateMetricFromModel(): void {
		const normalizedMetricSettingsData = this.normalizedMetricSettingsData;
		const model = this.mmMetricsService.createNewModel(normalizedMetricSettingsData);
		this.metric = model;
	}

	@action
	updateMetricTypeProperties(metricTypeProperties: MetricTypePropertiesType): void {
		this.updateMetricAndSave(metricTypeProperties);
	}

	@action
	openMetricDefinitionPopup(): void {
		if (this.isDisabled) {
			return;
		}

		const model = this.mmMetricsService.createNewModel(this.normalizedMetricSettingsData);

		this.mmMetricsService.openMetricDefinitionPopup(
			model,
			true,
			this.isPeriodDataTypeHidden,
			this.isAllValuesDataTypeHidden,
			undefined,
			(metricSettingsData: RecordData.Metric.Serialized) => {
				this.saveSettings(metricSettingsData);
			},
		);
	}

	@action
	changeEnsureUniquenessSettings(ensureUniquenessSettings: EnsureUniquenessSettings): void {
		this.updateMetricAndSave({
			ensureUnique: ensureUniquenessSettings.ensureUnique,
			isAppend: ensureUniquenessSettings.isAppend,
		});
	}

	@action
	changeHistoricalDataPersistence(value: boolean | null) {
		this.updateMetricAndSave({ isHistoricalDataPersistenceEnabled: value });
	}

	@action
	saveSettings(metricSettingsData: RecordData.Metric.Serialized): void {
		const parsedData = this.parseSettingsToSave(metricSettingsData);
		this.save(parsedData);
	}

	@action
	cleanupMetric(): void {
		this.metric.model.unloadRecord();
	}
}
